import { createContext, useContext, useState, type ReactNode } from "react";

const SOUND_STORAGE_KEY = "assistio.notifications.soundEnabled";

type NotificationPrefsContextValue = {
  soundEnabled: boolean;
  setSoundEnabled: (enabled: boolean) => void;
  notificationPermission: NotificationPermission | "unsupported";
  requestNotificationPermission: () => void;
};

const NotificationPrefsContext = createContext<NotificationPrefsContextValue | null>(null);

export function NotificationPrefsProvider({ children }: { children: ReactNode }) {
  const [soundEnabled, setSoundEnabledState] = useState(() => {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem(SOUND_STORAGE_KEY);
    return stored === null ? true : stored === "true";
  });

  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission | "unsupported">(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
    return Notification.permission;
  });

  const setSoundEnabled = (enabled: boolean) => {
    setSoundEnabledState(enabled);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(SOUND_STORAGE_KEY, String(enabled));
    }
  };

  const requestNotificationPermission = () => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    Notification.requestPermission().then((perm) => setNotificationPermission(perm));
  };

  return (
    <NotificationPrefsContext.Provider
      value={{ soundEnabled, setSoundEnabled, notificationPermission, requestNotificationPermission }}
    >
      {children}
    </NotificationPrefsContext.Provider>
  );
}

export function useNotificationPrefs(): NotificationPrefsContextValue {
  const ctx = useContext(NotificationPrefsContext);
  if (!ctx) throw new Error("useNotificationPrefs must be used within NotificationPrefsProvider");
  return ctx;
}
